// The right-hand pane of the Discover split-view: a full StrategyReport for the assembled
// (long+short) strategy, with a header (coin/interval + netting trade counts), a close button,
// and a "Use this strategy" footer that advances to Activate. Shared by both the Scan and Manual
// modes via the parent StepDiscover (state lives in useDraftBacktest).

import { Loader2, BarChart3, X, ArrowRight, LineChart } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StrategyReport } from "../../backtest/StrategyReport";
import { BacktestTradesChart } from "./BacktestTradesChart";
import type { BacktestResponseDto, CandleInterval, MarketType } from "../../../api/types";

interface Props {
  result: BacktestResponseDto | null;
  running: boolean;
  /** Whether an entry side is assembled — gates "Use this strategy". */
  hasStrategy: boolean;
  coin: string;
  interval: CandleInterval;
  marketType: MarketType;
  onClose: () => void;
  onAdvance: () => void;
}

export function BacktestPane({ result, running, hasStrategy, coin, interval, marketType, onClose, onAdvance }: Props) {
  const s = result?.strategy ?? null;
  const nettingCounts = s
    ? `${s.trades} trades · ${s.longTrades}L / ${s.shortTrades}S · ${s.reversals} rev`
    : null;

  return (
    <Card className="p-0 bg-card overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border flex-wrap">
        <div className="flex items-center gap-2 text-sm font-semibold min-w-0">
          <BarChart3 className="size-4 text-primary shrink-0" />
          <span className="truncate">Backtest result</span>
          <span className="text-[11px] font-normal text-muted-foreground font-mono truncate">
            {coin} {interval}{nettingCounts ? ` · ${nettingCounts}` : ""}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={onAdvance}
            disabled={!hasStrategy}
            size="sm"
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            Use this strategy
            <ArrowRight className="size-3.5" />
          </Button>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors cursor-pointer"
            title="Close backtest"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      {/* No inner height cap — the full report flows at the page's width like the Monitor tab,
          so every section (equity, returns, benchmark, risk, trades) is readable. */}
      <div className="p-4 space-y-4">
        {running && !result ? (
          <div className="py-16 text-center">
            <Loader2 className="size-6 mx-auto mb-3 text-primary animate-spin" />
            <p className="text-sm text-muted-foreground">
              Backtesting the assembled long + short strategy as one netting account…
            </p>
          </div>
        ) : result ? (
          <>
            {/* Price chart of the actual netting trades — at the TOP of the report so the first
                thing you see is WHERE the combined long+short strategy traded (entry/exit markers
                per trade on candles; the report's other charts are equity/returns, not price). */}
            {s && s.trades > 0 && s.entryTimes.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <LineChart className="size-4 text-primary" />
                  Trades on price
                  <span className="text-[11px] font-normal text-muted-foreground">— where each backtested trade entered &amp; exited</span>
                </div>
                <BacktestTradesChart
                  coin={coin}
                  interval={interval}
                  marketType={marketType}
                  entryTimes={s.entryTimes}
                  exitTimes={s.exitTimes}
                  directions={s.directions}
                  returns={s.returns}
                />
              </div>
            )}

            <StrategyReport result={result} />
          </>
        ) : (
          <p className="py-16 text-center text-sm text-muted-foreground">No backtest result.</p>
        )}
      </div>
    </Card>
  );
}
